import { CompleteEapPurchaseRequest, SalesService } from "@givenwell/management-api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { api, queryClient } from "./utils";

export type PurchaseStatus = Exclude<Parameters<(typeof SalesService)["getPurchases"]>[0]["status"], undefined>[number];
export type PurchaseType = Exclude<Parameters<(typeof SalesService)["getPurchases"]>[0]["type"], undefined>[number];
export type PassStatus = Exclude<Parameters<(typeof SalesService)["getPurchases"]>[0]["passStatus"], undefined>[number];

export const supplierPurchasesQueryKey = (supplierId: string) => ["suppliers", supplierId, "purchases"];

export const purchasesQuery = (
  supplierId: string,
  statuses: PurchaseStatus[] | undefined,
  types: PurchaseType[] | undefined,
  passStatuses: PassStatus[] | undefined,
  pageNumber: number,
  pageSize: number,
  from: Date | undefined,
  to: Date | undefined,
) => {
  const cleanFrom =
    from === undefined ? undefined : new Date(from.getFullYear(), from.getMonth(), from.getDate()).toISOString();
  const cleanTo =
    to === undefined
      ? undefined
      : new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 9999).toISOString();

  return queryOptions({
    queryKey: [
      ...supplierPurchasesQueryKey(supplierId),
      { statuses, types, pageNumber, pageSize, from: cleanFrom, to: cleanTo, passStatuses },
    ],
    queryFn: async () => {
      const response = await api.sales.getPurchases({
        supplierId,
        page: pageNumber,
        pageSize,
        status: statuses,
        type: types,
        from: cleanFrom,
        to: cleanTo,
        passStatus: passStatuses,
      });
      return response;
    },
    placeholderData: prev => prev,
  });
};

export const purchaseQuery = (supplierId: string, purchaseId: string) =>
  queryOptions({
    queryKey: ["sales-purchase", "detail", purchaseId, supplierId],
    queryFn: async () => {
      const response = await api.sales.getPurchase({ supplierId, purchaseId });
      return response.purchase;
    },
  });

export function useRedeemPassMutation() {
  return useMutation({
    mutationFn: async (args: { supplierId: string; purchaseId: string }) => {
      return await api.sales.redeemPass({ supplierId: args.supplierId, purchaseId: args.purchaseId });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: supplierPurchasesQueryKey(variables.supplierId) });
    },
  });
}

export type FulfilmentStatus = "pending" | "fulfilled" | "refunded" | "in_progress";
export function useFulfillOrderMutation() {
  return useMutation({
    mutationFn: async (args: { supplierId: string; purchaseId: string; status?: FulfilmentStatus; note?: string }) => {
      return await api.sales.fulfillOrder({
        supplierId: args.supplierId,
        purchaseId: args.purchaseId,
        status: args.status,
        note: args.note,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: supplierPurchasesQueryKey(variables.supplierId) });
    },
  });
}

export const eapPurchasesForFeedbackQuery = (supplierId: string) =>
  queryOptions({
    queryKey: ["eap-purchases-for-feedback", supplierId],
    queryFn: async () => {
      const response = await api.sales.getEapPurchasesForFeedback({ supplierId });
      return response;
    },
  });

export function useCompleteEapPurchaseMutation() {
  return useMutation({
    mutationFn: async ({
      supplierId,
      purchaseId,
      ...requestBody
    }: CompleteEapPurchaseRequest & { supplierId: string; purchaseId: string }) => {
      return await api.sales.completeEapPurchase({ supplierId, purchaseId, requestBody });
    },
  });
}
